import {
  LinkSystem,
  MyBenefitHeader, // eslint-disable-next-line
} from "@benefit-systems/hr-tools-ui";
// eslint-disable-next-line
import "@benefit-systems/shared/fonts/main.css";
import {
  isWebView,
  requestChangePin,
  requestToggleLocalAuth,
  useWebView,
} from "@benefit-systems/webview";
import { ThemeProvider } from "@mui/material";
import { ReactNode, useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";

import useLogout from "web/Pages/Customer/Content/Main/Logout/utils/useLogout";

import changeMetaLang from "web/Layout/Common/LanguageSwitcher/changeMetaLang";
import customTheme from "web/Layout/MainHeader/Desktop/MBHeader/theme/customTheme";
import __ from "web/Layout/Translations";
import {
  availableLanguages,
  defaultLanguage,
} from "web/Layout/Translations/LanguageWrapper";

import { IMPERSONATE_COOKIE_NAME } from "web/utils/system/cookie/const";
import removeCookie from "web/utils/system/cookie/removeCookie";

import { ombRedirect } from "web/constants/urls";

import { useGetCompanyDetailsQuery } from "web/features/company/companyApiSlice";
import {
  useGetCustomerDetailsQuery,
  useUpdateCustomerMutation,
} from "web/features/customer/customerApiSlice";
import { useGetDashboardHeaderDataQuery } from "web/features/dashboard/dashboardApiSlice";
import { flushReduxPersistCache } from "web/store";

const MBHeader = () => {
  const [companyLanguages, setCompanyLanguages] = useState<
    typeof availableLanguages
  >([]);
  const [lang, setLang] = useState(defaultLanguage);
  const { data: customer } = useGetCustomerDetailsQuery();
  const { language: userLanguage } = customer || {};
  const { data } = useGetCompanyDetailsQuery();
  const languages = data?.languages || [];
  const [updateCustomerFn] = useUpdateCustomerMutation();
  const { localAuthType, isLocalAuthEnabled } = useWebView();
  const [shouldDisplayPoints, setShouldDisplayPoints] = useState(true);

  const { data: dashboardMenu } = useGetDashboardHeaderDataQuery();
  const location = useLocation();
  const { logoutHandler } = useLogout();
  const { ssoRole, impersonated } = customer || {};
  const isAhr = ssoRole === "ahr";

  useEffect(() => {
    setCompanyLanguages(
      availableLanguages.filter(
        ({ key, code }) => languages?.includes(code) || key === defaultLanguage
      )
    );
  }, [data]);

  useEffect(() => {
    if (userLanguage) {
      changeMetaLang(userLanguage);
      setLang(userLanguage);
    }
  }, [userLanguage]);

  const handleLangChange = async (value: string | null) => {
    if (value !== null) {
      await flushReduxPersistCache();
      setLang(value);
      updateCustomerFn({
        language: value,
      });
    }
  };

  const backToOmbAsAhrHandler = () => {
    if (isAhr) {
      removeCookie(IMPERSONATE_COOKIE_NAME);
      window.open(
        `${ombRedirect.redirectAhrToOmb}?logoutUrl=${window.location.origin}/customer/account/logout`,
        "_self"
      );
    }
  };

  const backToOmbAsAdmHandler = () => {
    if (impersonated) {
      removeCookie(IMPERSONATE_COOKIE_NAME);
      window.open(ombRedirect.redirectOperatorToOmb, "_self");
    }
  };

  const handleChangePointsVisibility = () => {
    setShouldDisplayPoints(!shouldDisplayPoints);
  };

  const LinkComponent = useMemo(
    () =>
      ({
        to,
        children,
        ...props
      }: {
        to: string;
        children?: ReactNode;
        [key: string]: unknown;
      }) => {
        if (to.startsWith("http")) {
          return (
            <a {...props} href={to} rel="noreferrer">
              {children}
            </a>
          );
        }
        return (
          <Link to={to} {...props}>
            {children}
          </Link>
        );
      },
    []
  );

  return (
    <ThemeProvider theme={customTheme()}>
      <MyBenefitHeader
        mainMenu={dashboardMenu?.mainMenu || []}
        profileMenu={dashboardMenu?.profileMenu || []}
        productsMenu={dashboardMenu?.productsMenu || []}
        location={location}
        pointsBalance={dashboardMenu?.pointsBank?.balance || 0}
        logo={{
          companyName: dashboardMenu?.logo?.companyName || "",
          src: dashboardMenu?.logo?.src || "",
          url: dashboardMenu?.logo?.url || undefined,
        }}
        employee={{
          firstName: dashboardMenu?.employee?.firstName || " ",
          lastName: dashboardMenu?.employee?.lastName || " ",
          employeeId: "",
          email: dashboardMenu?.employee?.email || "",
          language: dashboardMenu?.employee?.language || "pl",
          position: "",
        }}
        isEmployeeCardEnabled={false}
        onLogout={logoutHandler}
        Link={LinkComponent}
        translate={__}
        isAhr={isAhr}
        dashboardUrl={process.env.MB_DASHBOARD_URL}
        originSystem={LinkSystem.CAFETERIA}
        isUserImpersonated={impersonated || false}
        onAdminRedirect={backToOmbAsAhrHandler}
        onOmbRedirect={backToOmbAsAdmHandler}
        lang={{ value: lang, companyLanguages, availableLanguages: languages }}
        {...(languages && { onLanguageChange: handleLangChange })}
        employeeUrl={`${process.env.MB_DASHBOARD_URL}/os/employees/me`}
        breakpoint={980}
        isMobileApp={isWebView()}
        isLocalAuthEnabled={isLocalAuthEnabled}
        localAuthType={localAuthType}
        onChangePin={requestChangePin}
        toggleLocalAuth={() => {
          requestToggleLocalAuth(!isLocalAuthEnabled);
        }}
        shouldPointsBeVisible={shouldDisplayPoints}
        onPointsVisibilityChange={handleChangePointsVisibility}
      />
    </ThemeProvider>
  );
};

export default MBHeader;
